import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { jwtDecode, JwtPayload } from 'jwt-decode';
import { BehaviorSubject, first, map, Observable, of, tap } from 'rxjs';
import { BackendService } from 'src/app/core/services/backend.service';
import { LangSetterService } from 'src/app/core/services/lang-setter.service';
import { TranslationsOverrideService } from 'src/app/core/services/translations-override.service';
import { Company } from 'src/app/models/admin-models/views/Company';
import { LoginUser, UserRoleENUM } from 'src/app/models/generic-models/DTOs/UserDTO';
import { PriceBreakdown } from 'src/app/models/generic-models/views/PriceBreakdown';
import { User } from 'src/app/models/generic-models/views/User';

@Injectable()
export class AuthService {
  private _token: string | null | undefined;
  private _user: BehaviorSubject<User | null> = new BehaviorSubject<User | null>(null);
  private _previewCompanySlug: string | undefined | null;

  public user$: Observable<User | null> = this._user.asObservable();

  constructor(
    private router: Router,
    private backendService: BackendService,
    private langSetterService: LangSetterService,
    private translationsOverrideService: TranslationsOverrideService,
  ) {
    this.initFromToken$()
      .pipe(first())
      .subscribe((user) => {
        if (user) this.createUser(user);
      });
  }

  private createPriceBreakdown(breakdown: PriceBreakdown | null) {
    if (breakdown) {
      return new PriceBreakdown(
        breakdown.id,
        breakdown.packing,
        breakdown.logistics,
        breakdown.margin,
        breakdown.customs,
        breakdown.tax,
        breakdown.rebate,
        breakdown.rvat,
      );
    }

    return undefined;
  }

  public createUser(user: LoginUser) {
    const priceBreakdown = this.createPriceBreakdown(user.priceBreakdown);

    this.user = new User(
      Number(user.id),
      user.username,
      user.companyId || '1',
      0,
      UserRoleENUM[user.roles as keyof typeof UserRoleENUM],
      user.resetPassword,
      priceBreakdown,
    );
  }

  /*private createCart(cart: string) {
    const cartObject = JSON.parse(cart) as CartItem[];
    if (cartObject && cartObject.length) {
      if (!cartObject[0].userId || user.id !== cartObject[0].userId) {
        localStorage.removeItem('cart');
      }
    }
  }*/

  public setLanguagesFromCompanyId(companyId: string) {
    this.backendService
      .getCompany$(companyId)
      .pipe(
        first(),
        map((companyDTO) => Company.fromDTO(companyDTO)),
      )
      .subscribe((company) => {
        this.translationsOverrideService.companyChanged(Company.toCompanySummary(company));
        this.langSetterService.setLanguagesFromCompany(company);
      });
  }

  public initFromToken$(): Observable<LoginUser | null> {
    if (this.isLoggedIn) return of(null);

    this._token = sessionStorage.getItem('token') || localStorage.getItem('token');

    if (this._token && !this.isTokenExpired(this._token)) {
      return this.backendService.getUser$().pipe(
        first(),
        tap((user) => {
          this.setLanguagesFromCompanyId(user.companyId);
        }),
        map((user) => {
          return user;
        }),
      );
    }

    return of(null);
  }

  public login$(username: string, password: string, stayLoggedIn = false): Observable<string | null> {
    return this.backendService.login$(username, password).pipe(
      first(),
      map((data) => {
        if (!data) return null;

        const user = data.user;
        this.setLanguagesFromCompanyId(user.companyId);
        const token = data.bearer;

        if (token) {
          if (stayLoggedIn) {
            localStorage.setItem('token', token);
          } else {
            sessionStorage.setItem('token', token);
          }

          this._token = token;
        } else {
          return null; // Return null if no token
        }

        if (user) this.createUser(user);

        /*const cart = localStorage.getItem('cart');
        if (cart) this.createCart(cart);*/

        return token;
      }),
    );
  }

  public logout(): void {
    localStorage.removeItem('token');
    localStorage.removeItem('cart');
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('ignore-pass-reset');
    this.user = null;
    this._token = null;
    this.router.navigate(['/login']);
  }

  private isTokenExpired(token: string): boolean {
    const decodedToken: JwtPayload = jwtDecode(token);
    const expirationDate = new Date(0);
    if (decodedToken.exp) expirationDate.setUTCSeconds(decodedToken.exp);
    return expirationDate < new Date();
  }

  public set user(u: User | null) {
    this._user.next(u);
  }

  public get previewCompanySlug(): string | null | undefined {
    return this._previewCompanySlug;
  }

  public set previewCompanySlug(n: string | null) {
    this._previewCompanySlug = n;
  }

  public get isLoggedIn(): boolean {
    return !!this.user;
  }

  public get token() {
    return this._token;
  }
}
